// Phase 14 — Targets management + split modal + achievement bars
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Split, Trash2, RefreshCw, Target as TargetIcon } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const DIMS = [
  "sales_value", "sku_qty", "sku_value", "category_qty",
  "distributor_value", "zone_value", "visit_count", "coverage_pct",
];
const PERIODS = ["month", "quarter", "year"];
const OWNER_TYPES = ["user", "zone", "distributor", "sku", "category"];

function AchievementBar({ pct, onTrack }) {
  const cls = pct == null ? "bg-neutral-300"
            : onTrack ? "bg-emerald-500" : "bg-rose-500";
  return (
    <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Money" }, { label: "Targets" }]}
        title="Targets"
        subtitle="Monthly / quarterly sales targets by user, zone or SKU."
        favorite={{ key: "admin.targets", type: "screen", label: "Targets", route: "/targets" }}
        testid="targetspage-page-header"
      />

      <div className={`h-full ${cls}`} style={{ width: `${Math.min(100, pct || 0)}%` }} />
    </div>
  );
}

function NewTargetDialog({ open, onClose, onCreated, users, zones }) {
  const [dim, setDim] = useState("sales_value");
  const [pt, setPt] = useState("month");
  const [pk, setPk] = useState(new Date().toISOString().slice(0, 7));
  const [ownerType, setOwnerType] = useState("user");
  const [ownerId, setOwnerId] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!value || Number(value) <= 0) { toast.error("target_value must be > 0"); return; }
    if (ownerType === "user" && !ownerId) { toast.error("Pick a user"); return; }
    setBusy(true);
    try {
      const body = {
        dimension: dim, period_type: pt, period_key: pk,
        owner_type: ownerType, target_value: Number(value),
      };
      if (ownerType === "user") body.owner_id = ownerId;
      if (zoneId) body.scope_zone_id = zoneId;
      await api.post("/targets", body);
      toast.success("Target created");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="new-target-dialog">
        <DialogHeader><DialogTitle>New target</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Dimension</Label>
              <Select value={dim} onValueChange={setDim}>
                <SelectTrigger data-testid="tgt-dim"><SelectValue /></SelectTrigger>
                <SelectContent>{DIMS.map(d => <SelectItem key={d} value={d}>{d.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Period type</Label>
              <Select value={pt} onValueChange={setPt}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PERIODS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Period key (e.g. 2026-08 / 2026-Q3 / 2026)</Label>
            <Input value={pk} onChange={(e) => setPk(e.target.value)} data-testid="tgt-period-key" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Owner type</Label>
              <Select value={ownerType} onValueChange={setOwnerType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{OWNER_TYPES.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {ownerType === "user" ? (
              <div>
                <Label>User</Label>
                <Select value={ownerId || "__pick__"} onValueChange={(v) => setOwnerId(v === "__pick__" ? "" : v)}>
                  <SelectTrigger data-testid="tgt-owner"><SelectValue placeholder="Pick user" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__pick__">— pick —</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role_code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label>Zone</Label>
                <Select value={zoneId || "__none__"} onValueChange={(v) => setZoneId(v === "__none__" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Any zone" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Any zone</SelectItem>
                    {zones.map(z => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div>
            <Label>Target value</Label>
            <Input type="number" min="1" value={value} onChange={(e) => setValue(e.target.value)} data-testid="tgt-value" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="tgt-save-btn">{busy ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SplitDialog({ open, target, users, onClose, onSaved }) {
  const [rows, setRows] = useState([{ owner_id: "", target_value: "" }]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setRows([{ owner_id: "", target_value: "" }]); }, [target?.id, open]);
  const sum = rows.reduce((a, r) => a + (Number(r.target_value) || 0), 0);
  const parentVal = target?.target_value || 0;
  const overflow = sum > parentVal;

  async function save() {
    for (const r of rows) {
      if (!r.owner_id) { toast.error("Pick owner for every row"); return; }
      if (!r.target_value || Number(r.target_value) <= 0) { toast.error("Every row needs a value > 0"); return; }
    }
    if (overflow) { toast.error(`Sum ${sum} exceeds parent ${parentVal}`); return; }
    setBusy(true);
    try {
      await api.post(`/targets/${target.id}/split`, {
        children: rows.map(r => ({ owner_id: r.owner_id, target_value: Number(r.target_value) })),
      });
      toast.success(`Split into ${rows.length} children`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="split-dialog">
        <DialogHeader>
          <DialogTitle>Split target</DialogTitle>
          <DialogDescription>
            Parent: {target?.dimension} · {target?.period_key} · target ₹{parentVal?.toLocaleString?.("en-IN")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-auto">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr,140px,32px] gap-2">
              <Select value={r.owner_id || "__pick__"} onValueChange={(v) => setRows(rs => rs.map((x, k) => k === i ? { ...x, owner_id: v === "__pick__" ? "" : v } : x))}>
                <SelectTrigger className="h-8" data-testid={`split-row-owner-${i}`}><SelectValue placeholder="Pick user" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__pick__">— pick —</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role_code}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" min="1" className="h-8" value={r.target_value} onChange={(e) => setRows(rs => rs.map((x, k) => k === i ? { ...x, target_value: e.target.value } : x))} data-testid={`split-row-value-${i}`} />
              <button onClick={() => setRows(rs => rs.filter((_, k) => k !== i))} className="text-rose-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setRows(r => [...r, { owner_id: "", target_value: "" }])} data-testid="split-add-row">+ Add row</Button>
        </div>
        <div className={`text-xs px-3 py-2 rounded ${overflow ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
          Sum: ₹{sum.toLocaleString("en-IN")} / Parent: ₹{parentVal.toLocaleString?.("en-IN") || 0}
          {overflow && " · overflow!"}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || overflow} data-testid="split-save-btn">{busy ? "Splitting…" : "Split"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TargetsPage() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [zones, setZones] = useState([]);
  const [creating, setCreating] = useState(false);
  const [splitting, setSplitting] = useState(null);
  const [filter, setFilter] = useState(new Date().toISOString().slice(0, 7));

  async function reload() {
    try {
      const [t, u, z] = await Promise.all([
        api.get(`/targets?page_size=100${filter ? `&period_key=${filter}` : ""}`),
        api.get("/users?page_size=200"),
        api.get("/zones"),
      ]);
      setItems(t.data.items || []); setUsers(u.data.items || []); setZones(z.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { reload(); }, [filter]);

  const roots = useMemo(() => items.filter(i => !i.parent_target_id), [items]);
  const children = useMemo(() => {
    const m = {};
    for (const i of items) if (i.parent_target_id) (m[i.parent_target_id] ||= []).push(i);
    return m;
  }, [items]);

  async function remove(t) {
    if (!window.confirm(`Delete target?`)) return;
    try { await api.delete(`/targets/${t.id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  function ownerLabel(t) {
    if (t.owner_type === "user") {
      const u = users.find(x => x.id === t.owner_id);
      return u ? `${u.name} · ${u.role_code}` : "user";
    }
    if (t.owner_type === "zone") {
      const z = zones.find(x => x.id === t.scope_zone_id);
      return z ? `Zone: ${z.name}` : "zone target";
    }
    return t.owner_type;
  }

  const RowFor = (t, indent = 0) => (
    <div key={t.id} className={`border rounded-md p-3 bg-white flex items-center gap-3 ${indent ? "ml-6" : ""}`} data-testid={`target-row-${t.id}`}>
      <TargetIcon className={`w-4 h-4 ${indent ? "text-neutral-400" : "text-[#E5197F]"}`} />
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate">{ownerLabel(t)}</div>
        <div className="text-xs text-neutral-500">
          {t.dimension} · {t.period_key} · target ₹{Number(t.target_value).toLocaleString("en-IN")}
          {t.achieved_value_cache != null && ` · achieved ₹${Number(t.achieved_value_cache).toLocaleString("en-IN")}`}
        </div>
        <div className="mt-1"><AchievementBar pct={t.achievement_pct_cache} onTrack /></div>
      </div>
      <div className="text-right w-16 shrink-0">
        <div className="font-semibold text-sm">{(t.achievement_pct_cache ?? 0).toFixed(0)}%</div>
      </div>
      {!indent && (
        <Button size="sm" variant="outline" onClick={() => setSplitting(t)} data-testid={`target-split-${t.id}`}>
          <Split className="w-3 h-3 mr-1" /> Split
        </Button>
      )}
      <Button size="sm" variant="outline" onClick={() => remove(t)}><Trash2 className="w-3 h-3 text-rose-500" /></Button>
    </div>
  );

  return (
    <div className="p-6 space-y-4" data-testid="targets-page">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">Targets</h1>
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="2026-08" className="h-8 w-32" data-testid="targets-period-filter" />
        <Button variant="ghost" size="sm" onClick={reload}><RefreshCw className="w-4 h-4" /></Button>
        <div className="flex-1" />
        <Button onClick={() => setCreating(true)} data-testid="targets-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New target
        </Button>
      </div>
      {roots.length === 0
        ? <div className="text-neutral-500 border border-dashed rounded-lg p-10 text-center">No targets for this period yet.</div>
        : roots.map(r => (
          <div key={r.id} className="space-y-1">
            {RowFor(r)}
            {(children[r.id] || []).map(c => RowFor(c, 1))}
          </div>
        ))}

      <NewTargetDialog open={creating} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); reload(); }} users={users} zones={zones} />
      <SplitDialog open={!!splitting} target={splitting} users={users} onClose={() => setSplitting(null)} onSaved={() => { setSplitting(null); reload(); }} />
    </div>
  );
}
